import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/domains';

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const category = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const filteredArticles = useMemo(() => {
    let list = slug === 'all' ? articles : articles.filter((a) => a.category === slug);
    if (sortBy === 'hot') {
      list = [...list].sort((a, b) => b.readCount - a.readCount);
    }
    return list;
  }, [slug, sortBy]);

  useEffect(() => {
    const catName = category?.name || '全部';
    document.title = `${catName} - ${siteInfo.name} | 黑料网最新资讯`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `浏览${catName}分类下的最新爆料和深度调查报道，黑料网为您提供真实深入的资讯内容。`);
  }, [category]);

  const categoryName = category?.name || '全部';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类浏览', to: '/category/all' },
                ...(slug !== 'all' ? [{ label: categoryName }] : []),
              ]}
            />
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl text-foreground-900 mb-2">
              {slug === 'all' ? '全部资讯' : categoryName}
            </h1>
            <p className="text-sm text-foreground-500">
              {slug === 'all'
                ? `共 ${articles.length} 篇报道，持续更新中`
                : `共 ${filteredArticles.length} 篇报道，持续更新中`}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mb-8">
            <CategoryTabs active={slug} />
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-500">排序：</span>
            <button
              onClick={() => setSortBy('latest')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                sortBy === 'latest'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-time-line mr-1"></i>最新发布
            </button>
            <button
              onClick={() => setSortBy('hot')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                sortBy === 'hot'
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-fire-fill mr-1"></i>最多阅读
            </button>
          </div>

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100">
                <i className="ri-inbox-line text-3xl text-secondary-500"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回全部资讯
              </Link>
            </div>
          )}

          {/* Pagination Placeholder */}
          {filteredArticles.length > 9 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap">1</button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">2</button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">3</button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}